import _styled, { default as _styled2 } from "styled-components";
import type { SanityCaseStudyCard } from "@ouster/shared";
import React from "react";
import { stripHtmlTags, takeFirst } from "~/features/app/utils";
import { CaseStudyMetaTags } from "~/features/case-study/case-study-meta-tags";
import { CaseStudyTags } from "~/features/case-study/case-study-tags";
import { EditorialCard } from "~/features/card/editorial-card";
import { EditorialFeaturedCard } from "~/features/card/editorial-featured-card";
import { up } from "~/features/app/screens";
import { hasRenderableSanityMedia, SanityMediaFigure } from "~/features/sanity/page-builder/sections/media-figure";
import { toHtml } from "~/features/sanity/rich-text/to-html";
import { useMediaQuery } from "~/features/ui/use-media-query";
export type CaseStudyCardViewProps = {
  linkText?: string;
  featured?: boolean;
  size?: "regular" | "large";
  ratio?: "310/260" | "496/280" | "fill";
  headline?: string | null;
  text?: string | null;
  url?: string | null;
  media?: React.ReactNode;
  tags?: React.ReactNode;
  meta?: React.ReactNode;
};
export function CaseStudyCardView(props: CaseStudyCardViewProps) {
  const {
    featured,
    linkText,
    size = "regular",
    headline,
    text,
    url,
    media,
    tags,
    meta
  } = props;
  const Card = featured ? EditorialFeaturedCard : EditorialCard;
  return <Card headline={headline ?? undefined} text={stripHtmlTags(text ?? "")} url={url ?? undefined} linkText={linkText} size={size} media={media} tags={tags} meta={meta} highlightIconOnHover />;
}
type Props = {
  caseStudy: SanityCaseStudyCard;
  linkText?: string;
  featured?: boolean;
  size?: "regular" | "large";
};

/** Renders a Case Studies overview card directly from the Sanity view contract. */
export function CaseStudyCard({
  caseStudy,
  featured,
  linkText,
  size = "regular"
}: Props) {
  const {
    headline,
    title,
    href,
    hero,
    industries,
    postDate,
    locations
  } = caseStudy;
  const {
    text,
    media
  } = takeFirst(hero) || {};
  const isLg = useMediaQuery(up("lg"));
  const html = text?.length ? toHtml(text) : undefined;
  return <CaseStudyCardView featured={featured} linkText={linkText} size={size} headline={headline || title} text={html ? stripHtmlTags(html) : undefined} url={href} media={hasRenderableSanityMedia(media) ?
  // Design 3.0 rounds listing card media by radius/sm; clipped here rather
  // than on the shared figure so other card types keep their square media.
  // The clip doubles as the frame for the hover zoom, which is keyed on the
  // card's `group` so it also fires when the pointer is over the card text.
  // Overlay controls sit outside this wrapper and so stay put.
  <_StyledDiv>
            <_StyledDiv2>
              <SanityMediaFigure media={media} ratio={isLg ? "496/280" : "310/260"} showCaption={false} sizes="lg:50vw" />
            </_StyledDiv2>
          </_StyledDiv> : undefined} tags={<CaseStudyTags industries={industries} />} meta={<CaseStudyMetaTags postDate={postDate} locations={locations} />} />;
}
var _StyledDiv = _styled("div").withConfig({
  componentId: "sc-sf-1h6ot6w-0"
})({
  "overflow": "hidden",
  "borderRadius": "4px"
});
var _StyledDiv2 = /*#__PURE__*/_styled2("div").withConfig({
  displayName: "case-study-card___StyledDiv2",
  componentId: "sc-12ymke5-0"
})({
  "transitionProperty": "transform",
  "transitionDuration": "500ms",
  "transitionTimingFunction": "ease-out",
  "@media (hover: hover)": {
    ".group:hover &": {
      "--tw-scale-x": "1.05",
      "--tw-scale-y": "1.05",
      "transform": "translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y))"
    }
  }
});